import React from 'react'
import { Box, Container, Typography, Link, Button, IconButton } from '@mui/material'
import { Email, Facebook, Twitter, LinkedIn, Instagram, YouTube } from '@mui/icons-material'
import { useTranslations } from 'next-intl'

interface FooterProps {
    currentYear: number
}

const Footer: React.FC<FooterProps> = ({ currentYear }) => {
    const t = useTranslations('footer')
    return (
        <Box
            component="footer"
            sx={{
                bgcolor: '#f5f7fa',
                color: '#6b7280',
                pt: 3,
                pb: 2,
                fontSize: '13px',
                fontFamily: 'Arial, sans-serif'
            }}
        >
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                {/* Top Section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 2.5,
                        borderBottom: '1px solid rgba(107,114,128,0.2)'
                    }}
                >
                    {/* Left Section - Subscribe */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography sx={{ fontSize: 13, color: '#6b7280', mr: 2 }}>{t('subscribeToNewOffers')}</Typography>
                    </Box>

                    {/* Center - Browse Hotels */}
                    <Link
                        href="/property/countries.en.html"
                        target="_blank"
                        sx={{
                            color: '#6b7280',
                            fontSize: 13,
                            textDecoration: 'none',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        {t('browseHotelsByCountry')}
                    </Link>

                    {/* Right - Social Icons */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            href="https://www.facebook.com/flyin.com"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                p: 0.5,
                                '&:hover': { color: '#3b5998' }
                            }}
                        >
                            <Facebook sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                            href="https://twitter.com/flyincom"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                p: 0.5,
                                '&:hover': { color: '#1da1f2' }
                            }}
                        >
                            <Twitter sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                            href="https://www.linkedin.com/company/flyin-com"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                p: 0.5,
                                '&:hover': { color: '#0077b5' }
                            }}
                        >
                            <LinkedIn sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                            href="https://www.instagram.com/flyincom/"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                p: 0.5,
                                '&:hover': { color: '#e4405f' }
                            }}
                        >
                            <Instagram sx={{ fontSize: 20 }} />
                        </IconButton>
                        <IconButton
                            href="https://www.youtube.com/user/FlyinVideos"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                p: 0.5,
                                '&:hover': { color: '#ff0000' }
                            }}
                        >
                            <YouTube sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Middle Section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 2.5,
                        borderBottom: '1px solid rgba(107,114,128,0.2)'
                    }}
                >
                    {/* Left Links */}
                    <Box sx={{ display: 'flex', gap: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                href="/contactUs.en.html"
                                target="_blank"
                                sx={{
                                    color: '#6b7280',
                                    textTransform: 'none',
                                    fontSize: 13,
                                    p: 0,
                                    minWidth: 'auto',
                                    justifyContent: 'flex-start',
                                    border: '1px solid rgba(107,114,128,0.3)',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 0.5,
                                    '&:hover': {
                                        bgcolor: 'rgba(107,114,128,0.1)',
                                        color: '#2563eb'
                                    }
                                }}
                            >
                                {t('contactUs')}
                            </Button>
                        </Box>
                        <Button
                            href="https://extranet.flyin.com/#/hotels/suppliers/edit"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                textTransform: 'none',
                                fontSize: 13,
                                p: 0,
                                minWidth: 'auto',
                                border: '1px solid rgba(107,114,128,0.3)',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 0.5,
                                height: 'fit-content',
                                '&:hover': {
                                    bgcolor: 'rgba(107,114,128,0.1)',
                                    color: '#2563eb'
                                }
                            }}
                        >
                            {t('addYourProperty')}
                        </Button>
                    </Box>

                    {/* Right - App Download */}
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography
                            sx={{
                                color: '#2563eb',
                                fontSize: 15,
                                fontWeight: 500,
                                mb: 0.5
                            }}
                        >
                            {t('flightAndHotelsInOnePlace')}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 1 }}>
                            {t('downloadAppNow')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Link
                                href="https://itunes.apple.com/us/app/flyin.com-tyran-w-fnadq/id1106268318"
                                target="_blank"
                            >
                                <Box
                                    component="img"
                                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                                    alt="Download on the App Store"
                                    sx={{ height: 40 }}
                                />
                            </Link>
                            <Link
                                href="https://play.google.com/store/apps/details?id=com.flyin.bookings"
                                target="_blank"
                            >
                                <Box
                                    component="img"
                                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                    alt="Get it on Google Play"
                                    sx={{ height: 40 }}
                                />
                            </Link>
                        </Box>
                    </Box>
                </Box>

                {/* License Numbers */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 2,
                        borderBottom: '1px solid rgba(107,114,128,0.2)'
                    }}
                >
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 0.5 }}>
                            {t('travelLicenseNumber')}
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#374151' }}>73100559</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 0.5 }}>
                            {t('licenseCategory')}
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#374151' }}>{t('travelAndTourismAgency')}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 0.5 }}>
                            {t('commercialRegistrationNumber')}
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#374151' }}>1010253765</Typography>
                    </Box>
                </Box>

                {/* Bottom Section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pt: 1.5
                    }}
                >
                    {/* Footer Links */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Link
                            href="/aboutUs.en.html"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                textDecoration: 'none',
                                '&:hover': { color: '#2563eb' }
                            }}
                        >
                            {t('aboutUs')}
                        </Link>
                        <Link
                            href="/faqs.en.html"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                textDecoration: 'none',
                                '&:hover': { color: '#2563eb' }
                            }}
                        >
                            {t('faq')}
                        </Link>
                        <Link
                            href="/privacyPolicy.en.html"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                textDecoration: 'none',
                                '&:hover': { color: '#2563eb' }
                            }}
                        >
                            {t('privacyPolicy')}
                        </Link>
                        <Link
                            href="/termsOfUse.en.html"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                textDecoration: 'none',
                                '&:hover': { color: '#2563eb' }
                            }}
                        >
                            {t('termsOfUse')}
                        </Link>
                        <Link
                            href="/howTo.en.html"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                textDecoration: 'none',
                                '&:hover': { color: '#2563eb' }
                            }}
                        >
                            {t('fileAClaim')}
                        </Link>
                        <Link
                            href="https://careers.flyin.com/"
                            target="_blank"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                textDecoration: 'none',
                                '&:hover': { color: '#2563eb' }
                            }}
                        >
                            {t('careers')}
                        </Link>
                    </Box>

                    {/* Payment and Copyright */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            component="img"
                            src="https://ui.cltp.co/flyinstatic/common/themes/v2/img/support-cards.png"
                            alt="Payment Methods"
                            sx={{ height: 25 }}
                        />
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                            {t('copyright', { year: currentYear })}
                        </Typography>
                        <Link href="https://ui.cltp.co/flyin-static/docs/flyin-VAT-cert.pdf" target="_blank">
                            <Box
                                component="img"
                                src="https://ui.cltp.co/flyin-static/img/vat-cert-logo.png"
                                alt="VAT"
                                sx={{ width: 24, height: 24 }}
                            />
                        </Link>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { Footer }
export type { FooterProps }
